/**
 * Margin Report Formulas Drawer
 *
 * Explains calculation formulas for each line in the margin report.
 * @see US-25d.12 - Composant MarginReportTable
 */

import { Drawer } from '@/components/ui';

interface MarginReportFormulasDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormulaSection {
  title: string;
  formulas: {
    name: string;
    formula: string;
    description: string;
  }[];
}

const formulaSections: FormulaSection[] = [
  {
    title: "Chiffre d'Affaires",
    formulas: [
      {
        name: 'Ventes Brutes HT',
        formula: 'SUM(ventes POS)',
        description:
          'Total des ventes hors taxes sur la periode, tous types de ventes confondus (normal, offert, repas employe).',
      },
      {
        name: 'Ventes Nettes HT',
        formula: 'Ventes Brutes - Offerts - Repas',
        description:
          'Ventes hors taxes apres deduction des offerts et repas employes. Sert de reference 100% pour tous les pourcentages du rapport.',
      },
    ],
  },
  {
    title: 'Stock',
    formulas: [
      {
        name: 'Stock Initial',
        formula: 'Inventaire + Mouvements',
        description:
          "Stock valorise au debut de la periode, base sur le dernier inventaire valide et les mouvements jusqu'a la date de debut.",
      },
      {
        name: 'Entrees',
        formula: 'SUM(receptions)',
        description:
          'Total des receptions de marchandises valorisees sur la periode.',
      },
      {
        name: 'Sorties',
        formula: 'SUM(transferts OUT)',
        description:
          'Total des transferts sortants vers autres sites sur la periode.',
      },
      {
        name: 'Stock Final',
        formula: 'Inventaire fin periode',
        description:
          "Stock valorise a la fin de la periode, base sur l'inventaire valide ou calcule par mouvements.",
      },
    ],
  },
  {
    title: 'Food Cost',
    formulas: [
      {
        name: 'FC Brut Theorique',
        formula: 'Consommation POS + Pertes',
        description:
          'Consommation theorique totale : ventes (tous types, au plein cout) + pertes declarees. Represente tout ce qui a ete consomme.',
      },
      {
        name: 'FC Brut Reel',
        formula: 'SI + Entrees - Sorties - SF',
        description:
          'Consommation reelle calculee par la variation de stock. Formule dynamique liee aux lignes Stock du tableau.',
      },
      {
        name: 'Marge Brute',
        formula: 'Ventes Nettes - FC Brut',
        description:
          'Marge degagee avant deductions. Theorique = ce que vous devriez avoir. Reelle = ce que vous avez vraiment.',
      },
    ],
  },
  {
    title: 'Deductions',
    formulas: [
      {
        name: 'Promotions / Offerts',
        formula: 'SUM(cout produits offerts)',
        description:
          "Cout de revient des produits offerts aux clients (promotions, gestes commerciaux). Deduit du food cost car ce n'est pas une perte.",
      },
      {
        name: 'Repas Personnel',
        formula: 'SUM(cout repas employes)',
        description:
          'Cout de revient des repas consommes par le personnel. Considere comme un avantage en nature, pas une perte.',
      },
      {
        name: 'Pertes connues',
        formula: 'Brut + Fini + Exceptionnel',
        description:
          'Somme des 3 types de pertes declarees. Formule dynamique liee aux sous-lignes du tableau.',
      },
    ],
  },
  {
    title: 'Food Cost Net',
    formulas: [
      {
        name: 'FC Net',
        formula: 'FC Brut - Promos - Repas - Pertes',
        description:
          "Food cost apres deduction des elements connus. L'ecart entre theorique et reel represente les pertes non identifiees.",
      },
    ],
  },
  {
    title: 'Ecarts',
    formulas: [
      {
        name: 'Ecart Total',
        formula: 'FC Net Reel - FC Net Theo',
        description:
          "Difference totale entre consommation reelle et theorique. Positif = surconsommation. C'est la \"demarque\" a analyser.",
      },
      {
        name: 'Ecart Bulk',
        formula: 'Ecart sur produits Bulk',
        description:
          'Ecart sur les produits vendus en vrac (frites, boissons fontaine...). Normal car difficile a doser precisement.',
      },
      {
        name: 'Ecart Non-Bulk',
        formula: 'Ecart sur produits Non-Bulk',
        description:
          "Ecart sur les produits comptes a l'unite. Cet ecart represente la vraie demarque inconnue a investiguer.",
      },
    ],
  },
];

export function MarginReportFormulasDrawer({
  open,
  onOpenChange,
}: MarginReportFormulasDrawerProps) {
  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Formules de calcul"
      description="Comprendre les calculs du rapport de marge"
      size="large"
    >
      <div className="space-y-8">
        {formulaSections.map((section) => (
          <div key={section.title}>
            <h3 className="text-sm font-semibold text-dark-500 mb-4 pb-2 border-b border-neutral-200">
              {section.title}
            </h3>
            <div className="space-y-4">
              {section.formulas.map((formula) => (
                <div
                  key={formula.name}
                  className="bg-neutral-50 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <span className="font-medium text-dark-500">
                      {formula.name}
                    </span>
                    <code className="text-xs bg-white px-2 py-1 rounded border border-neutral-200 text-primary-600 whitespace-nowrap">
                      {formula.formula}
                    </code>
                  </div>
                  <p className="text-sm text-neutral-600">{formula.description}</p>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Note explicative */}
        <div className="bg-primary-50 rounded-lg p-4 mt-6">
          <h4 className="text-sm font-medium text-primary-700 mb-2">
            Theorique vs Reel
          </h4>
          <p className="text-sm text-primary-600">
            Les valeurs <strong>theoriques</strong> sont calculees a partir des ventes
            et des recettes : ce que vous auriez du consommer.
            Les valeurs <strong>reelles</strong> sont calculees a partir des mouvements
            de stock : ce que vous avez reellement consomme.
            L'ecart entre les deux revele les pertes non identifiees.
          </p>
        </div>

        <div className="bg-amber-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-amber-700 mb-2">
            Interpretation des ecarts
          </h4>
          <ul className="text-sm text-amber-600 space-y-1">
            <li>• <strong>Ecart positif</strong> = surconsommation (perte)</li>
            <li>• <strong>Ecart negatif</strong> = sous-consommation (gain ou erreur de comptage)</li>
            <li>• <strong>Ecart Bulk</strong> = tolerable (dosage variable)</li>
            <li>• <strong>Ecart Non-Bulk</strong> = a investiguer (demarque inconnue)</li>
          </ul>
        </div>
      </div>
    </Drawer>
  );
}
